import React, { useState } from 'react';
import { IonButton, IonIcon } from '@ionic/react';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';
import {
  format, addDays, addMonths,
  startOfWeek, startOfMonth, endOfMonth, endOfWeek,
  isSameDay, isSameMonth,
} from 'date-fns';
import styles from './MiniCalendarPicker.module.scss';
import { dfLocale } from '../i18n/locale';

interface Props {
  value: Date | null;
  onSelect: (date: Date) => void;
  desktop?: boolean;
}

const MiniCalendarPicker: React.FC<Props> = ({ value, onSelect, desktop = false }) => {
  const [viewMonth, setViewMonth] = useState<Date>(value ?? new Date());

  const monthStart = startOfMonth(viewMonth);
  const calStart   = startOfWeek(monthStart);
  const calEnd     = endOfWeek(endOfMonth(viewMonth));

  const days: Date[] = [];
  let d = calStart;
  while (d <= calEnd) { days.push(d); d = addDays(d, 1); }

  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  const today = new Date();

  return (
    <div className={`${styles.calendar}${desktop ? ` ${styles.desktop}` : ''}`}>
      <div className={styles.header}>
        <IonButton fill="clear" size="small" className={styles.navBtn}
          onClick={() => setViewMonth(m => addMonths(m, -1))}>
          <IonIcon icon={chevronBackOutline} />
        </IonButton>
        <span className={styles.title}>{format(viewMonth, 'MMMM yyyy', { locale: dfLocale() })}</span>
        <IonButton fill="clear" size="small" className={styles.navBtn}
          onClick={() => setViewMonth(m => addMonths(m, 1))}>
          <IonIcon icon={chevronForwardOutline} />
        </IonButton>
      </div>

      <table className={styles.grid}>
        <thead>
          <tr>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(h => (
              <th key={h} className={styles.dayHead}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map(day => {
                const isToday    = isSameDay(day, today);
                const isSelected = value ? isSameDay(day, value) : false;
                const isCurrent  = isSameMonth(day, viewMonth);
                return (
                  <td
                    key={day.getTime()}
                    className={[
                      styles.day,
                      isToday    ? styles.today    : '',
                      isSelected ? styles.selected : '',
                      !isCurrent ? styles.otherMonth : '',
                    ].join(' ')}
                    onClick={() => onSelect(day)}
                  >
                    <span className={styles.dateNum}>{day.getDate()}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MiniCalendarPicker;
